import { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';

import { ServerStateKeysEnum } from 'reactQuery/constants';
import { cartService } from 'services';
import { AddLineItemParams, Cart } from 'types';
import { displayNotification, pushToGTM } from 'utils';
import { useI18n } from '../../contexts/i18n';

export const useAddLineItem = () => {
  const i18n = useI18n();

  const queryClient = useQueryClient();
  return useMutation(
    ServerStateKeysEnum.addLineitemMutation,
    ({
      variantId,
      quantity,
      description,
      code,
      projectId,
      setShowAddToCartConfirmation,
      isRedeemPointSelected,
      options,
    }: AddLineItemParams) => {
      return quantity
        ? cartService.addLineitem(
            variantId,
            description,
            code,
            quantity,
            isRedeemPointSelected,
            projectId,
            options
          )
        : cartService.addLineitem(variantId, description, code);
    },
    {
      onSuccess: (data, variables, context) => {
        variables.setShowAddToCartConfirmation(true);
        queryClient.setQueriesData([ServerStateKeysEnum.userCurrentCart], data);
        queryClient.invalidateQueries([ServerStateKeysEnum.doctorCreditCards]);
        pushAddToCartEventToGTM(data);
      },
      onError: (err: AxiosError<{ error: string }>) => {
        // If the error is a 422, it means the quantity limit has been reached and special modal should be shown in the product page instead of displayNotification
        if (err.response?.status !== 422) {
          const errMessage =
            err?.response?.data?.error ?? i18n['Failed to add to Cart!'];
          displayNotification(errMessage, 'danger');
        }
      },
    }
  );
};

const pushAddToCartEventToGTM = (data: Cart) => {
  const currentLineItem = data.lineItems[data.lineItems.length - 1];

  pushToGTM('add_to_cart', {
    ecommerce: {
      currency: data.currency,
      value: currentLineItem.price,
      items: [
        {
          item_id: currentLineItem.slug,
          item_name: currentLineItem.name,
          affiliation: 'Global Store NA',
          price: currentLineItem.price,
          currency: data.currency,
          quantity: currentLineItem.quantity,
        },
      ],
    },
  });
};
